import { PromoReel } from '../components/PromoReel';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Truck, 
  Users, 
  DollarSign, 
  FileText, 
  Shield, 
  Clock,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  Menu,
  X,
  Star,
  Zap,
  Globe,
  TrendingUp,
  Calculator,
  Bell,
  Share2,
  Mail,
  Phone,
  MapPin,
  Send,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.subject || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSendingMessage(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, contactForm);
      toast.success(response.data.message || 'Message sent successfully!');
      setContactForm({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const features = [
    {
      icon: Truck,
      title: 'Unit & Fleet Management',
      description: 'Track all your trucks, trailers, and equipment in one place. Monitor maintenance schedules and keep your fleet running smoothly.',
      color: 'bg-blue-500'
    },
    {
      icon: Users,
      title: 'Driver Management',
      description: 'Manage driver profiles, documents, licenses, and compliance. Never miss an expiration date with automated alerts.',
      color: 'bg-emerald-500'
    },
    {
      icon: DollarSign,
      title: 'Settlement & Payouts',
      description: 'Automatically calculate dispatch fees, deductions, and net payouts. Generate professional settlement PDFs instantly.',
      color: 'bg-violet-500'
    },
    {
      icon: FileText,
      title: 'Load Tracking',
      description: 'Track every load from pickup to delivery. Record broker info, routes, dates, and payouts all in one place.',
      color: 'bg-amber-500'
    },
    {
      icon: BarChart3,
      title: 'Reports & Analytics',
      description: 'Visualize your earnings with beautiful charts. Track weekly, monthly, and yearly performance at a glance.',
      color: 'bg-rose-500'
    },
    {
      icon: Bell,
      title: 'Smart Alerts',
      description: 'Get automated email reminders for expiring documents, upcoming maintenance, and important deadlines.',
      color: 'bg-cyan-500'
    }
  ];

  const stats = [
    { value: '72%', label: 'of US freight moved by trucks', icon: Truck },
    { value: '3.5M', label: 'truck drivers in America', icon: Users },
    { value: '$940B', label: 'trucking industry revenue', icon: DollarSign },
    { value: '11.84B', label: 'tons of freight annually', icon: Globe }
  ];

  const benefits = [
    'Customizable dispatch fee per unit (flat or %)',
    'Professional PDF settlements with your logo',
    'Weekly & YTD financial tracking',
    'Driver schedule with drag-and-drop contracts',
    'Bank statement reconciliation with AI',
    'Company accounting & P&L dashboard',
    'Driver document management & compliance',
    'Company branding on all reports'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-2">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">My Dispatch</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-blue-600 transition-colors">Features</a>
              <a href="#stats" className="text-slate-600 hover:text-blue-600 transition-colors">Industry Stats</a>
              <a href="#benefits" className="text-slate-600 hover:text-blue-600 transition-colors">Benefits</a>
              <a href="#contact" className="text-slate-600 hover:text-blue-600 transition-colors">Contact Us</a>
              <Link to="/login">
                <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Get Started Free
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <div className="px-4 py-4 space-y-4">
              <a href="#features" className="block text-slate-600 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#stats" className="block text-slate-600 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Industry Stats</a>
              <a href="#benefits" className="block text-slate-600 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Benefits</a>
              <a href="#contact" className="block text-slate-600 hover:text-blue-600" onClick={() => setMobileMenuOpen(false)}>Contact Us</a>
              <div className="flex gap-3 pt-4">
                <Link to="/login" className="flex-1">
                  <Button variant="outline" className="w-full border-blue-600 text-blue-600">Sign In</Button>
                </Link>
                <Link to="/register" className="flex-1">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">Get Started</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image - White Freightliner Cascadia */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1616432043562-3671ea2e5242?auto=format&fit=crop&w=2000&q=80"
            alt="White Freightliner Cascadia Truck"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-1.5 mb-6">
              <Zap className="h-4 w-4 text-blue-400" />
              <span className="text-sm text-blue-300">Trusted by dispatch companies nationwide</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Simplify Your
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400"> Trucking </span>
              Operations
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
              The all-in-one dispatch management platform for owner-operators and fleet managers. 
              Track loads, manage drivers, calculate settlements, and grow your business.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button size="lg" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Sign In to Dashboard
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-6 mt-10 pt-10 border-t border-white/10">
              <div className="flex -space-x-2">
                {[1,2,3,4].map(i => (
                  <div key={i} className={`w-10 h-10 rounded-full border-2 border-slate-900 bg-gradient-to-br ${i === 1 ? 'from-blue-400 to-blue-600' : i === 2 ? 'from-emerald-400 to-emerald-600' : i === 3 ? 'from-amber-400 to-amber-600' : 'from-rose-400 to-rose-600'}`} />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-slate-400">Loved by dispatch teams everywhere</p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
            <div className="w-1.5 h-3 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      <PromoReel />

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need to Run Your Fleet
            </h2>
            <p className="text-lg text-slate-600">
              Powerful features designed specifically for trucking companies, owner-operators, and dispatch teams.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden">
                <CardContent className="p-6">
                  <div className={`${feature.color} w-14 h-14 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=2000&q=80"
            alt="Highway"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/90" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The Backbone of America
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              The trucking industry keeps America moving. Here's why what you do matters.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition-all duration-300">
                  <stat.icon className="h-10 w-10 text-blue-400 mx-auto mb-4" />
                  <div className="text-4xl sm:text-5xl font-bold text-white mb-2">{stat.value}</div>
                  <p className="text-slate-400">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-slate-400 text-sm">
              Source: American Trucking Associations (ATA) 2024 Industry Report
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Save Time. Reduce Errors.
                <span className="text-blue-600"> Get Paid Faster.</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                My Dispatch automates the tedious parts of running a trucking business so you can focus on what matters - keeping your trucks moving and your drivers happy.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/register">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8">
                    Get Started Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-3xl p-8 lg:p-12">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
                  alt="Dashboard"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              
              {/* Floating Cards */}
              <div className="absolute -left-4 top-1/4 bg-white rounded-xl shadow-xl p-4 animate-float">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Weekly Net</p>
                    <p className="font-bold text-slate-900">$12,450</p>
                  </div>
                </div>
              </div>

              <div className="absolute -right-4 bottom-1/4 bg-white rounded-xl shadow-xl p-4 animate-float-delayed">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Calculator className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Dispatch Fee</p>
                    <p className="font-bold text-slate-900">Customizable</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Frequently Asked Questions</h2>
            <p className="text-lg text-slate-600 mt-4">Everything you need to know about My Dispatch</p>
          </div>
          <div className="space-y-4">
            {[
              { q: 'What is My Dispatch?', a: 'My Dispatch is an all-in-one trucking management platform designed for dispatch companies, owner-operators, and fleet managers. It handles load tracking, driver settlements, scheduling, accounting, and compliance — all in one place.' },
              { q: 'How does the dispatch fee work?', a: 'You can set a custom dispatch fee per unit — either as a percentage of the gross payout or a flat dollar amount per load. The system automatically calculates fees for every load and generates professional settlement PDFs.' },
              { q: 'Can I manage driver schedules and contracts?', a: 'Yes! The Driver Schedule page features a visual Gantt-style calendar where you can assign contracts, track time off, fill gaps with loads, and drag-and-drop to reassign between drivers. You can also print or share schedules as PDFs.' },
              { q: 'Does it handle bank statement reconciliation?', a: 'Absolutely. Upload your bank statement PDF and our AI will extract and categorize every transaction — matching Amazon deposits, driver payouts, fuel charges, insurance, and more. Unmatched items get flagged for manual review.' },
              { q: 'Can different users see different features?', a: 'Yes. Admins can toggle features on/off at the company level, and also control per-user access. For example, you can hide the Accounting page from drivers while keeping it visible for dispatchers.' },
              { q: 'Is my data secure?', a: 'Your data is encrypted in transit and at rest. We use industry-standard security practices including JWT authentication, password hashing with bcrypt, and secure cloud infrastructure.' },
              { q: 'Can I brand the settlements with my company logo?', a: 'Yes! Upload your company logo in Company Settings and it will appear on all exported PDFs — settlement pay stubs, schedules, and reports.' },
            ].map((faq, i) => (
              <details key={i} className="group border rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors">
                  <span className="font-semibold text-slate-900">{faq.q}</span>
                  <ChevronRight className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-90" />
                </summary>
                <p className="px-5 pb-5 pt-2 text-slate-600 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Get in Touch
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Have questions about My Dispatch? Need help getting started? Our team is here to help you succeed.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-xl">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Email Us</h3>
                    <p className="text-slate-600">info@support-my-dispatch.com</p>
                    <p className="text-sm text-slate-500">We'll respond within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-emerald-100 p-3 rounded-xl">
                    <Phone className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Call Us</h3>
                    <p className="text-slate-600">Available Mon-Fri, 9am-5pm EST</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-violet-100 p-3 rounded-xl">
                    <MapPin className="h-6 w-6 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Based in</h3>
                    <p className="text-slate-600">United States</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="shadow-xl border-0">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-slate-900 mb-6">Send us a message</h3>
                  <form onSubmit={handleContactSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contact-name">Your Name</Label>
                        <Input
                          id="contact-name"
                          placeholder="John Doe"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-email">Email Address</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          placeholder="john@example.com"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contact-company">Company</Label>
                        <Input
                          id="contact-company"
                          placeholder="Your trucking company"
                          value={contactForm.company}
                          onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-phone">Phone (optional)</Label>
                        <Input
                          id="contact-phone"
                          placeholder="(555) 123-4567"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-subject">Subject</Label>
                      <Input
                        id="contact-subject"
                        placeholder="How can we help?"
                        value={contactForm.subject}
                        onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-message">Message</Label>
                      <Textarea
                        id="contact-message"
                        placeholder="Tell us more about your needs..."
                        rows={5}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700 py-6"
                      disabled={sendingMessage}
                    >
                      {sendingMessage ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="h-4 w-4" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Streamline Your Dispatch?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Join thousands of trucking professionals who trust My Dispatch to manage their operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="w-full sm:w-auto bg-white text-blue-600 hover:bg-blue-50 px-10 py-6 text-lg">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10 px-10 py-6 text-lg">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Truck className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">My Dispatch</span>
            </div>
            
            <div className="flex items-center gap-8 text-slate-400">
              <a href="#features" className="hover:text-white transition-colors">Features</a>
              <a href="#stats" className="hover:text-white transition-colors">Stats</a>
              <a href="#benefits" className="hover:text-white transition-colors">Benefits</a>
              <a href="#contact" className="hover:text-white transition-colors">Contact Us</a>
            </div>

            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} My Dispatch. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 3s ease-in-out infinite;
          animation-delay: 1.5s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
